import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

class VariantProductAccordions extends Component {
  #observer;
  #scheduled = false;
  #region = null;
  #lastContent = null;
  #lastSignature = null;

  connectedCallback() {
    super.connectedCallback();

    this.#rememberRegion();

    // Watch for content arriving after this component connects,
    // including content replaced by a variant update.
    this.#observer = new MutationObserver(() => {
      this.#scheduleRegionalPlants();
    });

    this.#observer.observe(this, {
      childList: true,
      subtree: true,
      characterData: true
    });

    this.#scheduleRegionalPlants();

    this.section = this.closest('.shopify-section, dialog');

    if (!this.section) return;

    this.section.addEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );
  }

  disconnectedCallback() {
    this.#observer?.disconnect();

    this.section?.removeEventListener(
      StandardEvents.productSelect,
      this.#handleProductSelect
    );

    this.#lastContent = null;
    this.#lastSignature = null;

    super.disconnectedCallback();
  }

  #rememberRegion() {
    const region = new URLSearchParams(
      window.location.search
    ).get('region');

    if (region && /^[1-5]$/.test(region)) {
      this.#region = region;
    }

    // Keep the initial region if the theme later removes it
    // while updating the variant URL.
  }

  #scheduleRegionalPlants() {
    if (this.#scheduled) return;

    this.#scheduled = true;

    queueMicrotask(() => {
      this.#scheduled = false;

      if (!this.isConnected) return;

      this.#applyRegionalPlants();
    });
  }

  #applyRegionalPlants() {
    this.#rememberRegion();

    // No valid region: preserve the original variant content.
    if (!this.#region) return;

    const jsonElement = this.querySelector(
      'script[data-regional-plants]'
    );

    // No regional metafield: preserve the original behavior.
    if (!jsonElement) return;

    const rawJSON = jsonElement.textContent.trim();

    if (!rawJSON) return;

    let regionalPlants;

    try {
      regionalPlants = JSON.parse(rawJSON);

      // Also support JSON saved as a serialized text value.
      if (typeof regionalPlants === 'string') {
        regionalPlants = JSON.parse(regionalPlants);
      }
    } catch (error) {
      console.warn(
        '[variant-product-accordions] Could not read regional plant JSON.',
        error
      );

      return;
    }

    if (
      !regionalPlants ||
      typeof regionalPlants !== 'object' ||
      Array.isArray(regionalPlants)
    ) {
      return;
    }

    const regionValue = regionalPlants[this.#region];

    const entries = Array.isArray(regionValue)
      ? regionValue
      : typeof regionValue === 'string'
        ? [regionValue]
        : [];

    if (
      entries.length === 0 ||
      !entries.every((entry) => typeof entry === 'string')
    ) {
      return;
    }

    const plants = entries
      .flatMap((entry) => entry.split('|'))
      .map((plant) => plant.trim())
      .filter(Boolean);

    // Missing or empty region: keep the variant's original list.
    if (plants.length === 0) return;

    const accordion = this.querySelector(
      'details[data-accordion="2"]'
    );

    const content = accordion?.querySelector(
      '[data-accordion-content="2"]'
    );

    if (!accordion || !content) return;

    const signature = JSON.stringify([
      this.#region,
      plants
    ]);

    // Avoid repeatedly rendering our own DOM changes.
    if (
      this.#lastContent === content &&
      this.#lastSignature === signature
    ) {
      return;
    }

    const list = document.createElement('ul');
    list.className = 'product-media-accordion__plants';

    for (const plant of plants) {
      const item = document.createElement('li');

      item.className = 'product-media-accordion__plant';
      item.textContent = plant;

      list.appendChild(item);
    }

    this.#lastContent = content;
    this.#lastSignature = signature;

    content.replaceChildren(list);
    accordion.hidden = false;
  }

  #handleProductSelect = (event) => {
    if (
      !(event.target instanceof Element) ||
      event.target.closest('product-card')
    ) {
      return;
    }

    // Capture the region before the variant URL changes.
    this.#rememberRegion();

    event.promise
      .then(({ detail }) => {
        if (!this.isConnected || !detail?.html) return;

        if (
          detail.productId &&
          this.dataset.productId &&
          String(detail.productId) !== this.dataset.productId
        ) {
          return;
        }

        const blockId = this.dataset.blockId;

        if (!blockId) return;

        const newAccordions = detail.html.querySelector(
          `variant-product-accordions[data-block-id="${CSS.escape(blockId)}"]`
        );

        this.#lastContent = null;
        this.#lastSignature = null;

        if (!newAccordions) {
          this.replaceChildren();
          return;
        }

        // Restore the original variant content first.
        this.innerHTML = newAccordions.innerHTML;

        // Apply the optional regional override afterward.
        this.#scheduleRegionalPlants();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[variant-product-accordions] Event promise rejected:',
            error
          );
        }
      });
  };
}

if (!customElements.get('variant-product-accordions')) {
  customElements.define(
    'variant-product-accordions',
    VariantProductAccordions
  );
}